import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus, Trash2 } from 'lucide-react'
import { useShipments } from '@/hooks/useShipments'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { SHIPMENT_STATUS } from '@/lib/constants'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { shipmentsApi } from '@/lib/api'
import { useState } from 'react'

export function ShipmentListPage() {
  const { data: shipments, isLoading, refetch, isFetching } = useShipments()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    if (!confirm('Создать новую отгрузку?')) return
    setIsCreating(true)
    try {
      const { data } = await shipmentsApi.create()
      const result = data as { shipment?: { id?: string }; success?: boolean }
      toast.success('Отгрузка создана')
      if (result.shipment?.id) {
        navigate(`/admin/shipments/${result.shipment.id}`)
      } else {
        refetch()
      }
    } catch {
      toast.error('Ошибка создания отгрузки')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(`Удалить отгрузку ${id}?`)) return
    try {
      await shipmentsApi.delete(id)
      toast.success('Отгрузка удалена')
      refetch()
    } catch {
      toast.error('Ошибка удаления отгрузки')
    }
  }

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Отгрузки</h1>
        <div className="flex gap-2">
          <Button onClick={handleCreate} isLoading={isCreating}>
            <Plus className="w-4 h-4 mr-1" /> Создать отгрузку
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!shipments?.length ? (
        <div className="text-center text-gray-500 py-20">
          <p className="text-lg">Нет отгрузок</p>
          <p className="text-sm mt-1">Отгрузки создаются при отправке палет из задания</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Комментарий</th>
                <th className="px-4 py-3">Позиций</th>
                <th className="px-4 py-3">Создал</th>
                <th className="px-4 py-3">Дата</th>
                <th className="px-4 py-3">Экспорт</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {shipments.map(s => {
                const meta = SHIPMENT_STATUS[s.status]
                return (
                  <tr key={s.id} className="hover:bg-gray-50" onClick={() => navigate(`/admin/shipments/${s.id}`)}>
                    <td className="px-4 py-3 font-mono text-xs">{s.id}</td>
                    <td className="px-4 py-3">
                      <StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[12rem] truncate" title={s.comment || undefined}>
                      {s.comment?.trim() ? s.comment : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {s.additions_count}
                      <span className="text-gray-400 text-xs"> / {s.items_count}</span>
                    </td>
                    <td className="px-4 py-3">{s.created_by}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDateTime(s.created_at)}</td>
                    <td className="px-4 py-3">
                      {s.exported ? <span className="text-green-600 text-xs font-medium">Выгружено</span> : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {s.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(s.id);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
